import { Location } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppInformationService, AuthService, GenesisUnitOfWork, InvoiceService, UtilityService } from 'app/core/core.module';
import { ReportService } from 'app/core/data/report.service';
import {
    Dealer,
    FeeType,
    Invoice,
    InvoiceAttachment,
    InvoiceLine,
    InvoiceStatus,
    Order,
    OrderLineDetail,
    PackingSlip,
    PaymentLine,
    ShipAddress,
} from 'app/core/model/entity-model';
import { Utility } from 'app/core/utility';
import { OrderBuildDetailComponent } from 'app/orders/order-build-detail/order-build-detail.component';
import { OrderDetailComponent } from 'app/orders/order-detail/order-detail.component';
import { AuditDialogComponent } from 'app/shared/audit-dialog/audit-dialog.component';
import { EmailDialogComponent } from 'app/shared/email-dialog/email-dialog.component';
import { InputDialogComponent } from 'app/shared/input-dialog/input-dialog.component';
import { SharedModule } from 'app/shared/shared.module';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import { environment } from 'environments/environment';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-invoice-detail',
    templateUrl: './invoice-detail.component.html',
    styleUrls: ['./invoice-detail.component.scss'],
    standalone: false,
})
export class InvoiceDetailComponent implements OnInit {
    result: Subject<any> = new Subject<any>();
    invoiceId: any;
    orderIds: number[];
    order: Order;
    packingSlipIds: number[];
    packingSlip: PackingSlip;
    dealer: Dealer;
    orderLineDetails: any[];

    invoice: any;
    invoiceLines: any;
    invoiceAttachments: InvoiceAttachment[];
    preSubmittedNote: string;
    dealers: Dealer[];
    paymentMethods: any;
    deliveryOptions: any;
    shipAddresses: ShipAddress[];
    readOnly = true;

    uploadHeader: any;
    uploadUrl = `${environment.serviceName}/file/SaveInvoiceAttachmentToBlobStorage`;

    processing = false;
    showAsDialog = false;
    saving = false;

    canUpdatePricing = false;
    canAddDetails = false;
    achInfo: any;
    paymentLines: PaymentLine[];
    invoiceCredits: any[];
    feeTypes: FeeType[];
    ready = false;

    notesList: any[];
    isInvoiceCredit: boolean = false;

    @ViewChild('linesGrid') linesGrid: DxDataGridComponent;
    onExporting = SharedModule.onExporting;

    constructor(
        private uow: GenesisUnitOfWork,
        private route: ActivatedRoute,
        private router: Router,
        public auth: AuthService,
        private modalService: BsModalService,
        private appInfo: AppInformationService,
        protected location: Location,
        private reportService: ReportService,
        private utility: UtilityService,
        private invoiceService: InvoiceService,
    ) {}

    // @HostListener allows us to also guard against browser refresh, close, etc.
    @HostListener('window:beforeunload')
    canDeactivate() {
        return !this.hasChanges();
    }

    async ngOnInit() {
        if (!this.invoiceId) this.invoiceId = this.route.snapshot.params.id;

        this.uploadHeader = { Authorization: `Bearer ${this.auth.currentUser.jwtToken}` };
        this.feeTypes = (await this.uow.feeTypes.whereActive('displayOrder')) as FeeType[];
        this.paymentMethods = await this.uow.invoicePaymentMethods.whereActive('displayOrder');
        const statuses = this.uow.lookups.invoiceStatuses;
        const types = this.uow.lookups.paymentLineTypes;
        const partTypes = this.uow.lookups.partTypes;

        if (this.invoiceId === 'new') {
            let details;
            let detailIds = [];

            this.orderIds = this.route.snapshot.queryParams.orderIds ? JSON.parse(this.route.snapshot.queryParams.orderIds) : null;
            this.packingSlipIds = this.route.snapshot.queryParams.packingSlipIds ? JSON.parse(this.route.snapshot.queryParams.packingSlipIds) : null;
            if (this.orderIds) {
                this.order = (await this.uow.orders.byId(this.orderIds[0], 'dealer')) as Order;
                this.dealer = this.order.dealer;
                details = await this.uow.getOrderLineDetailsForInvoicingByOrderIds(this.orderIds);
                detailIds = details.nonInvoicedOrderLineDetails.map((d) => d.id);
            }
            if (this.packingSlipIds) {
                this.packingSlip = (await this.uow.packingSlips.byId(this.packingSlipIds[0], 'dealer')) as PackingSlip;
                this.dealer = this.packingSlip.dealer;
                details = await this.uow.getOrderLineDetailsForInvoicingByPackingSlipIds(this.packingSlipIds);
                detailIds = details.nonInvoicedOrderLineDetails.map((d) => d.id);
            }

            this.orderLineDetails = await this.uow.orderLineDetails.byIds(
                detailIds,
                'orderLine.order, orderLine.part.partPartTypes, orderLine.order.dealer, orderLine.order.dealer.shipAddresses',
            );
            await this.createNewInvoice();
            if (details.nonInvoicedFreightPackingSlips.length !== 0) {
                const packingSlips = (await this.uow.packingSlips.byIds(details.nonInvoicedFreightPackingSlips)) as PackingSlip[];
                packingSlips.forEach((packingSlip) => {
                    this.createFreightLine(packingSlip);
                });
            }
            this.calcTotals();
        } else {
            await this.getData();
        }

        this.setTitle();
        this.setPermissions();
        this.ready = true;
        this.linesGrid?.instance.refresh();
    }
    hasChanges() {
        return this.uow.hasChanges();
    }

    setTitle() {
        const title = `${this.invoice.dealer.companyName} - Invoice #${this.invoice.invoiceNumber}`;
        this.appInfo.title = title;
    }

    setPermissions() {
        this.canUpdatePricing = this.auth.hasRole('SalesOrder:CanUpdatePricingAfterSubmit');
        this.canAddDetails = this.auth.hasRole('Invoices:CanAddDetails');
        this.isInvoiceCredit = this.invoice.statusId === InvoiceStatus.Statuses.Credit;
    }
    async refresh() {
        this.appInfo.pleaseWaitShow();
        await this.getData();
        this.calcTotals();
        this.appInfo.pleaseWaitHide();
    }
    async createNewInvoice() {
        this.invoice = this.uow.invoices.createEntity({
            created: new Date(),
            createdBy: this.auth.currentUser.userName,
            invoiceDate: moment().toDate(),
            dueDate: moment().add(30, 'days').toDate(),
            statusId: 1,
            dealerId: this.dealer.id,
            tax: 0,
            freight: 0,
            total: 0,
            subTotal: 0,
            balance: 0,
            feesTotal: 0,
            reportGuid: crypto.randomUUID(),
        }) as any;

        if (this.orderLineDetails.length !== 0) {
            this.orderLineDetails.forEach((detail, index) => {
                this.invoice.invoiceLines.push(
                    this.uow.invoiceLines.createEntity({
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                        partNumber: detail.orderLine.partNumber,
                        description: detail.orderLine.description,
                        isTaxable: detail.tax !== 0,
                        lineNumber: index + 1,
                        price: detail.price,
                        quantity: detail.quantity,
                        orderLineDetailId: detail.id,
                    }) as InvoiceLine,
                );
                detail.invoiceId = this.invoice.id;
            });
        }

        this.invoice = this.invoice;
        this.invoiceLines = this.invoice.invoiceLines;
        this.invoiceService.sortInvoiceLines(this.invoiceLines);
    }
    async createFreightLine(packingSlip: PackingSlip) {
        const trailerDetails = this.orderLineDetails
            .filter((detail) => detail.parentLineId === null && detail.packingSlipId == packingSlip.id)
            .map((detail) => `  Part#: ${detail.orderLine.partNumber}\r\n  VIN: ${detail.vIN}`)
            .join('\r\n');
        this.invoice.invoiceLines.push(
            this.uow.invoiceLines.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                partNumber: 'Freight',
                description: `Freight charge for Packing Slip# ${packingSlip.packingSlipNumber}\r\n${trailerDetails}`,
                isTaxable: false,
                lineNumber: this.invoice.invoiceLines.length + 1,
                price: packingSlip.freight,
                quantity: 1,
                orderLineDetailId: null,
            }) as InvoiceLine,
        );
        packingSlip.freightInvoiceId = this.invoice.id;
        packingSlip.freightInvoice = this.invoice;
        this.invoiceLines = this.invoice.invoiceLines;
    }

    async getData() {
        this.invoice = (await this.uow.invoices.byId(
            this.invoiceId,
            'paymentLines.payment, dealer.shipAddresses, invoiceLines.orderLineDetail.packingSlip, invoiceLines.orderLineDetail.orderLine.order, invoiceLines.orderLineDetail.orderLine.part, packingSlipFreights',
        )) as Invoice;

        this.notesList = await this.uow.getInvoiceNotes(this.invoice.id);

        this.shipAddresses = !!this.invoice.dealer ? this.invoice.dealer.shipAddresses : [];
        this.dealer = this.invoice.dealer;
        this.invoice.shipCompanyName = this.invoice.dealer ? this.invoice.dealer.companyName : '';
        this.preSubmittedNote = this.invoice.note;

        this.invoiceLines = this.invoice.invoiceLines.filter((line) => !line.deleted);
        this.paymentLines = this.invoice.paymentLines.filter((line) => !line.deleted);
        this.invoiceCredits = await this.uow.getPaymentCreditsForInvoiceId(this.invoice.id, this.invoice.dealerId, 'creditOrder.orderLines.returnedDetail, paymentCreditType');
        await this.getAttachments();
    }

    async getAttachments() {
        this.invoiceAttachments = await this.uow.getInvoiceAttachments(this.invoice.id);
    }
    onInvoiceDateChanged(e) {
        this.invoice.dueDate = moment(e.value).add(30, 'days').toDate();
    }

    async updateLinePrice() {
        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (this.invoice.quickBooksId) {
            const result = await confirm(
                `This Invoice has already been exported to QuickBooks, if you continue, you will need to update pricing in QuickBooks. Continue?`,
                `${this.appInfo.settings.company}`,
            );
            if (!result) return;
        }

        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            initialState: {
                dialogHeader: `${this.appInfo.settings.company}`,
                dialogPrompt: 'New Price',
                dialogValue: selectedRow.price,
                checkNumeric: true,
                checkInteger: false,
                mode: 'number',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result && result.result) {
                this.appInfo.pleaseWaitShow();
                selectedRow.price = result.value;
                await this.uow.commit();
                await this.getData();
                this.calcTotals();

                //check the new total against the payment lines and see if the status needs to be changed
                const newStatusId = await this.invoiceService.updateInvoiceStatus(this.invoice.invoiceLines, this.invoice.paymentLines);
                console.log(newStatusId);
                if (newStatusId !== this.invoice.statusId) {
                    this.invoice.statusId = newStatusId;
                    if (newStatusId === InvoiceStatus.Statuses.Paid) this.invoice.paid = new Date();
                    if (newStatusId === InvoiceStatus.Statuses.PartiallyPaid) this.invoice.paid = null;
                }

                if (this.invoice.quickBooksLastExport) this.invoice.quickBooksLastExport = null;
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
            }
            modalRef.hide();
        });
    }

    async addFeeLine() {
        const modalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Fees',
                dialogPrompt: 'Select Fee',
                dialogValue: 1,
                dialogValueArray: this.feeTypes,
                mode: 'selectWithId',
            },
        });

        const promise = new Promise<any>(async (resolve, reject) => {
            modalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                modalRef.hide();
                resolve(null);
            });
        });

        const selectedFeeId = await Promise.resolve(promise);
        if (!selectedFeeId) return;

        this.appInfo.pleaseWaitShow();
        const feeType = _.find(this.feeTypes, { id: selectedFeeId });
        const fee = feeType.percentageBased ? Utility.round(this.invoice.invoiceTotal * feeType.defaultAmount, 2) : feeType.defaultAmount;

        this.invoice.invoiceLines.push(
            this.uow.invoiceLines.createEntity({
                created: new Date(),
                createdBy: this.auth.currentUser.userName,
                partNumber: `Fee`,
                description: feeType.name,
                isTaxable: false,
                lineNumber: this.invoice.invoiceLines.length + 1,
                price: fee,
                quantity: 1,
                orderLineDetailId: null,
                feeTypeId: feeType.id,
            }) as InvoiceLine,
        );

        await this.uow.commit();
        await this.getData();
        this.calcTotals();
        if (this.invoice.quickBooksLastExport) this.invoice.quickBooksLastExport = null;
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    async removeLine() {
        const selectedRow = this.linesGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        const result = await confirm(`Are you sure you want to Delete the selected line?`, `${this.appInfo.settings.company}`);
        if (!result) return;

        this.appInfo.pleaseWaitShow();
        selectedRow.deleted = new Date();
        selectedRow.deletedBy = this.auth.currentUser.userName;

        if (selectedRow.orderLineDetailId) {
            const orderLineDetail = (await this.uow.orderLineDetails.byId(selectedRow.orderLineDetailId)) as OrderLineDetail;
            orderLineDetail.invoiceId = null;
        }
        if (selectedRow.partNumber?.toLowerCase() === 'freight') {
            const packingSlips = await this.uow.getPackingSlipByFreightInvoiceId(selectedRow.invoiceId);
            packingSlips.forEach((packingSlip) => {
                packingSlip.freightInvoiceId = null;
            });
        }

        await this.uow.commit();
        await this.getData();
        this.calcTotals();
        if (this.invoice.quickBooksLastExport) this.invoice.quickBooksLastExport = null;
        this.invoiceService.sortInvoiceLines(this.invoiceLines);
        await this.uow.commit();
        this.linesGrid.instance.refresh();
        this.appInfo.pleaseWaitHide();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                location: 'before',
                template: 'toolbarText',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            },
            {
                widget: 'dxButton',
                options: {
                    icon: 'refresh',
                    elementAttr: { title: 'Reset Grid Layout' },
                    onClick: () => {
                        e.component.state({});
                    },
                },
                location: 'after',
            },
        );
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            e.cells.forEach((cell) => {
                if (cell.column.dataField === 'balance') {
                    if (cell.value === 0) cell.cellElement.className = cell.cellElement.className + ' line-zero-balance';
                    else cell.cellElement.className = cell.cellElement.className + ' line-open-balance';
                }
            });
        }
    }

    async checkInvoiceStatus() {
        //check the new total against the payment lines and see if the status needs to be changed
        const newStatusId = await this.invoiceService.updateInvoiceStatus(this.invoice.invoiceLines, this.invoice.paymentLines);
        if (newStatusId !== this.invoice.statusId) {
            this.invoice.statusId = newStatusId;
            if (newStatusId === InvoiceStatus.Statuses.Paid) this.invoice.paid = new Date();
            if (newStatusId === InvoiceStatus.Statuses.PartiallyPaid) this.invoice.paid = null;
        }
    }

    calcTotals() {
        // console.log('calcTotal');
        this.calcSubTotal();
        this.calcTax();
        this.calcFreight();

        if (this.invoice.total !== Utility.round(this.invoice.subTotal + this.invoice.tax, 2)) this.invoice.total = Utility.round(this.invoice.subTotal + this.invoice.tax, 2);
        this.checkInvoiceStatus();
    }

    calcSubTotal() {
        if (this.invoice.subTotal !== Utility.round(this.invoice.invoiceSubTotal, 2)) this.invoice.subTotal = Utility.round(this.invoice.invoiceSubTotal, 2);
    }

    calcTax() {
        if (this.invoice.tax !== Utility.round(this.invoice.invoiceTaxTotal, 2)) this.invoice.tax = Utility.round(this.invoice.invoiceTaxTotal, 2);
    }
    calcFreight() {
        if (
            this.invoice.freight !==
            Utility.round(
                this.invoice.invoiceLines.filter((line) => line.partNumber?.toLowerCase() === 'freight').reduce((a, b) => a + b.price, 0),
                2,
            )
        )
            this.invoice.freight = Utility.round(
                this.invoice.invoiceLines.filter((line) => line.partNumber?.toLowerCase() === 'freight').reduce((a, b) => a + b.price, 0),
                2,
            );
    }

    async save() {
        this.appInfo.pleaseWaitShow();
        this.calcTotals();
        await this.uow.commit();

        if (this.invoiceId === 'new') {
            this.invoice.invoiceNumber = await this.utility.getNextInvoiceNumber();
            this.invoice.readyToExport = new Date();
            await this.uow.commit();
            this.invoiceId = this.invoice.id;
            this.router.navigate(['/invoices/' + this.invoice.id], { replaceUrl: true });
            this.setTitle();
            await this.getData();
        }

        this.appInfo.pleaseWaitHide();
    }

    cancel() {
        this.uow.rollback();
        if (this.invoiceId === 'new') this.close();
    }

    close() {
        if (this.showAsDialog) {
            this.result.next({});
        } else {
            try {
                this.location.back();
            } catch {
                this.router.navigate(['../'], { relativeTo: this.route });
            }
        }
    }
    closeDialog() {
        if (this.showAsDialog) {
            this.uow.rollback();
            this.result.next({});
        }
    }

    validate() {
        // const groupConfig = validationEngine.getGroupConfig(this.validationGroup);
        // if (groupConfig) {
        //     const result = validationEngine.validateGroup(this.validationGroup);
        //     if (!result.isValid) {
        //         let message = '';
        //         result.brokenRules.forEach((brokenRule) => {
        //             if (brokenRule.type === 'custom') message = `${brokenRule.message}\n${message}`;
        //         });
        //         this.appInfo.notifyToast(`Please fix errors before ${this.errorAction}.\n\n${message}`, 'error');
        //         return false;
        //     }
        // }
        // return true;
    }

    openPayment(paymentLine: any) {
        console.log(paymentLine);
        this.router.navigate(['/payments/' + paymentLine.paymentId]);
    }

    openOrderDialog(orderId: number) {
        if (!orderId) return;

        const modalRef = this.modalService.show(OrderDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                showAsDialog: true,
                orderId,
            },
        });

        modalRef.content.result.subscribe(() => {
            modalRef.hide();
        });
    }

    async print(type: 'Invoice' | 'Pack Slip') {
        if (type === 'Pack Slip') this.reportService.openPackingSlip([this.invoice.reportGuid]);
        else if (type === 'Invoice') {
            if (!this.invoice.reportGuid) {
                this.invoice.reportGuid = crypto.randomUUID();
                await this.uow.commit();
            }

            this.reportService.openInvoiceReport([this.invoice.reportGuid]);
        }
    }

    async emailInvoice() {
        const body = ((await this.uow.getSettingsByName('InvoiceEmailBody')) as any).stringValue;

        const modalRef = this.modalService.show(EmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'Invoice',
                entityId: this.invoice.id,
                dealerEmail: this.invoice.dealer.aPEmail ?? this.invoice.dealer.email,
                body: body,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success && !!result.value) {
                this.appInfo.pleaseWaitShow();
                const emailSent = await this.utility.emailReport(result.value);
                this.invoice.emailed = new Date();
                await this.uow.commit();
                this.appInfo.pleaseWaitHide();
                await alert(emailSent ? 'Email Sent!' : 'An error occurred while sending the email.', this.appInfo.settings.company);
            }
            modalRef.hide();
        });
    }

    async removeAttachment(e: any, attachment: InvoiceAttachment) {
        e.preventDefault();
        attachment.deleted = new Date();
        this.invoiceAttachments.splice(this.invoiceAttachments.indexOf(attachment), 1);
        await this.uow.commit([attachment]);
    }

    openAttachment(attachment) {
        window.open(attachment.filename, '_blank');
    }

    uploadStarted(e) {
        this.appInfo.pleaseWaitShow();
    }

    async uploaded(e) {
        if (e?.request?.responseText) {
            const fileResponses = JSON.parse(e.request.responseText) as { filename: string; name: string }[];
            for (const fileResponse of fileResponses) {
                const attachment = this.uow.invoiceAttachments.createEntity({
                    invoiceId: this.invoice.id,
                    name: fileResponse.name,
                    filename: fileResponse.filename,
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                }) as InvoiceAttachment;
                this.invoiceAttachments.push(attachment);
                await this.uow.commit([attachment]);
            }
            this.invoiceAttachments = _(this.invoiceAttachments).orderBy('name').valueOf();
        }
        this.appInfo.pleaseWaitHide();
    }

    uploadError(e) {
        this.appInfo.pleaseWaitHide();
        alert('An error occurred while attempting to upload the file(s)', 'Upload Error');
    }

    async readyToExport() {
        this.invoiceService.readyToExport([this.invoice]);
        await this.getData();
    }

    async deleteInvoice() {
        const result = await this.invoiceService.deleteInvoices([this.invoice.id]);
        if (result) this.close();
    }

    async receivePayment(credit?: boolean) {
        this.invoiceService.receivePayment(this.invoice, credit);
    }

    async createCredit() {
        this.invoice.statusId = 6;
        await this.uow.commit();
        this.setPermissions();
    }

    async cancelCredit() {
        await this.checkInvoiceStatus();
        await this.uow.commit();
        this.setPermissions();
    }

    async creditLines() {
        const selectedRows = this.linesGrid.instance.getSelectedRowsData();
        if (selectedRows.length === 0) {
            alert('Please select at least one line to credit.', `${this.appInfo.settings.company}`);
            return;
        }

        //get a reason from them why they are crediting an invoice
        const reasonModalRef = this.modalService.show(InputDialogComponent, {
            class: 'modal-sm',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                dialogHeader: 'Credit Reason',
                dialogPrompt: 'Please provide a reason for crediting the selected line(s):',
                dialogValue: '',
                mode: 'textarea',
            },
        });
        const reasonPromise = new Promise<string>(async (resolve, reject) => {
            reasonModalRef.content.result.subscribe(async (result) => {
                if (result.result && !!result.value) {
                    resolve(result.value);
                }
                reasonModalRef.hide();
                resolve(null);
            });
        });
        const creditReason = await Promise.resolve(reasonPromise);
        if (!creditReason) return;

        const modalRef = this.modalService.show(OrderDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                orderId: 'invoice-credit',
                invoice: this.invoice,
                showAsDialog: true,
                invoiceCreditLines: selectedRows,
                creditReason: creditReason,
                paymentLines: this.invoice.paymentLines,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                console.log('result', result);

                //get the order line details for the created order and add them to the invoice
                const orderLineDetails = (await this.uow.getOrderLineDetailByOrderId(result.orderId, 'orderLine')) as OrderLineDetail[];
                console.log('orderLineDetails', orderLineDetails);
                orderLineDetails.forEach((detail) => {
                    const invoiceLine = this.uow.invoiceLines.createEntity({
                        invoiceId: this.invoice.id,
                        orderLineDetailId: detail.id,
                        lineNumber: Invoice.getNextLineNumber(this.invoice.invoiceLines),
                        partNumber: detail.orderLine.partNumber,
                        description: detail.orderLine.description,
                        quantity: detail.quantity,
                        price: detail.price,
                        isTaxable: detail.tax !== 0,
                        created: new Date(),
                        createdBy: this.auth.currentUser.userName,
                    }) as InvoiceLine;
                    this.invoice.invoiceLines.push(invoiceLine);
                    detail.invoiceId = this.invoice.id;
                });
                await this.uow.commit();
                await this.getData();
                this.calcTotals();
            }
            modalRef.hide();
        });
    }

    async openSalesOrder(id: number) {
        this.router.navigate([`/sales-orders/${id}`]);
    }

    async addInvoiceLine() {
        if (this.invoice.statusId === InvoiceStatus.Statuses.Paid) {
            const result = await confirm('This invoice is already paid. Are you sure you want to continue modifying it?', this.appInfo.settings.company);
            if (!result) return;
        }
        if (this.invoice.quickBooksId) {
            const result = await confirm(
                `This Invoice has already been exported to QuickBooks, if you continue, you will need to update QuickBooks. Continue?`,
                `${this.appInfo.settings.company}`,
            );
            if (!result) return;
        }

        if (this.invoice.emailed) {
            const result = await confirm(`This Invoice was already emailed, if you continue, you will need to resend the email. Continue?`, `${this.appInfo.settings.company}`);
            if (!result) return;
        }

        // Add your logic to add a new invoice line here
        const modalRef = this.modalService.show(OrderBuildDetailComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                mode: 'invoice',
                invoiceId: this.invoice.id,
                showAsDialog: true,
                storageKey: 'invoice-build-detail-grid',
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                this.appInfo.pleaseWaitShow();
                const orderLineDetail = (await this.uow.orderLineDetails.byId(result.orderLineDetailId, 'optionDetails')) as OrderLineDetail;
                orderLineDetail.invoiceId = this.invoice.id;
                const invoiceLine = this.uow.invoiceLines.createEntity({
                    created: new Date(),
                    createdBy: this.auth.currentUser.userName,
                    partNumber: orderLineDetail.orderLine.partNumber,
                    description: orderLineDetail.orderLine.description,
                    isTaxable: orderLineDetail.tax !== 0,
                    lineNumber: this.invoice.invoiceLines.length + 1,
                    price: orderLineDetail.price,
                    quantity: orderLineDetail.quantity,
                    orderLineDetailId: orderLineDetail.id,
                }) as InvoiceLine;
                this.invoice.invoiceLines.push(invoiceLine);

                orderLineDetail.optionDetails
                    .filter((option) => !option.deleted && !option.invoiceId)
                    .forEach((option) => {
                        // Add your logic here for non-deleted options
                        option.invoiceId = this.invoice.id;
                        const invoiceLine = this.uow.invoiceLines.createEntity({
                            created: new Date(),
                            createdBy: this.auth.currentUser.userName,
                            partNumber: option.orderLine.partNumber,
                            description: option.orderLine.description,
                            isTaxable: option.tax !== 0,
                            lineNumber: this.invoice.invoiceLines.length + 1,
                            price: option.price,
                            quantity: option.quantity,
                            orderLineDetailId: option.id,
                        }) as InvoiceLine;
                        this.invoice.invoiceLines.push(invoiceLine);
                    });

                await this.uow.commit();
                this.invoiceService.sortInvoiceLines(this.invoice.invoiceLines);
                if (this.invoice.quickBooksLastExport) this.invoice.quickBooksLastExport = null;
                await this.uow.commit();
                await this.getData();
                this.calcTotals();
                this.appInfo.pleaseWaitHide();
                // await this.linesGrid.instance.refresh();
            }
            modalRef.hide();
        });
    }

    async auditLog() {
        const auditLogs = [];

        const invoiceAudits = await this.uow.getHistory('Invoice', [this.invoice.id]);
        invoiceAudits.forEach((x) => auditLogs.push(x));

        const paymentLines = await this.uow.getPaymentLinesByInvoiceId(this.invoice.id);
        const paymentLineIds = paymentLines.map((x: any) => x.id);
        if (paymentLineIds.length > 0) {
            const paymentLineAudits = await this.uow.getHistory('PaymentLine', paymentLineIds);
            paymentLineAudits.forEach((x) => auditLogs.push(x));
        }

        const paymentIds = paymentLines.map((x: any) => x.payment?.id);
        const uniquePaymentIds = [...new Set(paymentIds)] as any;
        if (uniquePaymentIds.length > 0) {
            const paymentAudits = await this.uow.getHistory('Payment', uniquePaymentIds);
            paymentAudits.forEach((x) => auditLogs.push(x));
        }

        const modalRef = this.modalService.show(AuditDialogComponent, {
            class: 'modal-lg',
            initialState: {
                auditLogs: auditLogs,
            },
        });

        modalRef.content.result.subscribe(() => {
            modalRef.hide();
        });
    }
}
